/**
 * Group Permission Utilities
 * Helper functions for checking user permissions within groups
 */

import { GroupRole, GroupPermissionLevel } from '@api/types/social'

/**
 * Check if a user can perform an action based on their role and the required permission level
 * @param userRole - User's role in the group (null if not a member)
 * @param hasMatchingTags - Whether the user has matching interest tags with the group
 * @param permissionLevel - Required permission level for the action
 * @returns true if user can perform the action
 */
export function canUserPerformAction(
  userRole: GroupRole | null | undefined,
  hasMatchingTags: boolean,
  permissionLevel: GroupPermissionLevel
): boolean {
  // Not a member
  if (userRole === null || userRole === undefined) {
    if (permissionLevel === GroupPermissionLevel.Anyone) return true
    if (permissionLevel === GroupPermissionLevel.MatchingTags && hasMatchingTags) return true
    return false
  }

  // Is member - check role hierarchy
  const roleHierarchy: Record<GroupRole, number> = {
    [GroupRole.Member]: 2,
    [GroupRole.Moderator]: 3,
    [GroupRole.Admin]: 4,
    [GroupRole.Owner]: 5,
  }

  return roleHierarchy[userRole] >= permissionLevel
}

/**
 * Get display name for permission level
 */
export function getPermissionLevelName(level: GroupPermissionLevel): string {
  switch (level) {
    case GroupPermissionLevel.Anyone:
      return 'Anyone'
    case GroupPermissionLevel.MatchingTags:
      return 'Matching Tags'
    case GroupPermissionLevel.Members:
      return 'Members'
    case GroupPermissionLevel.Moderators:
      return 'Moderators'
    case GroupPermissionLevel.Admins:
      return 'Admins'
    case GroupPermissionLevel.Owner:
      return 'Owner Only'
    default:
      return 'Members'
  }
}

/**
 * Get description for permission level
 */
export function getPermissionLevelDescription(level: GroupPermissionLevel): string {
  switch (level) {
    case GroupPermissionLevel.Anyone:
      return 'Anyone can perform this action'
    case GroupPermissionLevel.MatchingTags:
      return 'Only users with matching interest tags'
    case GroupPermissionLevel.Members:
      return 'Only group members'
    case GroupPermissionLevel.Moderators:
      return 'Moderators and above'
    case GroupPermissionLevel.Admins:
      return 'Admins and above'
    case GroupPermissionLevel.Owner:
      return 'Only the group owner'
    default:
      return 'Only group members'
  }
}

/**
 * Get permission descriptions for settings UI
 */
export const permissionDescriptions = {
  viewMembers: 'Who can see the member list',
  inviteUsers: 'Who can invite new members to the group',
  viewTags: "Who can see the group's interest tags",
  createDiscussions: 'Who can start new discussion threads',
  participateInDiscussions: 'Who can reply to discussions',
  viewDiscussions: 'Who can read discussion threads',
  accessChat: 'Who can access and send messages in group chat',
}

/**
 * Get friendly error message when permission is denied
 */
export function getPermissionDeniedMessage(permissionLevel: GroupPermissionLevel, action: string): string {
  const levelName = getPermissionLevelName(permissionLevel)

  switch (permissionLevel) {
    case GroupPermissionLevel.Anyone:
      return `${action} is available to anyone`
    case GroupPermissionLevel.MatchingTags:
      return `Only users with matching interest tags can ${action.toLowerCase()}. Make sure your profile tags align with this group's interests!`
    case GroupPermissionLevel.Members:
      return `Only group members can ${action.toLowerCase()}. Join the group to participate!`
    case GroupPermissionLevel.Moderators:
      return `Only moderators can ${action.toLowerCase()}`
    case GroupPermissionLevel.Admins:
      return `Only admins can ${action.toLowerCase()}`
    case GroupPermissionLevel.Owner:
      return `Only the group owner can ${action.toLowerCase()}`
    default:
      return `You don't have permission to ${action.toLowerCase()}`
  }
}
